// @flow
import { httpRequest, httpRequestWithToken, getAvaBackendEndpoint } from '../../utils/http';

const patchUser = async ({
  avaId,
  firebaseAuthUID,
  data,
}: {
  avaId: string,
  firebaseAuthUID: string,
  data: Object,
}) => {
  return httpRequestWithToken({
    url: getUsersUrl(avaId, 'v1.1', firebaseAuthUID),
    method: 'PATCH',
    payload: data,
  });
};

const updateMetrics = async ({
  avaId,
  firebaseAuthUID,
  convoMetrics,
}: {
  avaId: string,
  firebaseAuthUID: string,
  convoMetrics: Object,
}) => patchUser({ avaId, firebaseAuthUID, data: { convoMetrics } });

// Updates the user's feature flags.
//
// The database refers to this value as 'flags', while the application
// state refers to this as features.
// TODO: Should update the application state to refer to user's features
// as either featureFlags or just flags.
const updateFeatures = async ({
  avaId,
  firebaseAuthUID,
  features,
}: {
  avaId: string,
  firebaseAuthUID: string,
  features: Object,
}) => patchUser({ avaId, firebaseAuthUID, data: { flags: features } });

const updateUserName = async ({
  avaId,
  firebaseAuthUID,
  userName,
}: {
  avaId: string,
  firebaseAuthUID: string,
  userName: string,
}) => patchUser({ avaId, firebaseAuthUID, data: { userName } });

const updateHearingProfile = async ({
  avaId,
  firebaseAuthUID,
  hearingProfile,
}: {
  avaId: string,
  firebaseAuthUID: string,
  hearingProfile: number,
}) => {
  // NOTE: There are 3 main hearing profiles (deaf, hard-of-hearing, hearing)
  // and 2 other sub profiles. Since it was not part of the design to implement
  // all 5 profiles, this is hard-coded for now. If we do implement all 5
  // profiles, then we can use HEARING_PROFILE in utils/user.js
  if (!(hearingProfile >= 0 && hearingProfile <= 4)) {
    throw new Error('Invalid hearing profile');
  }

  return patchUser({ avaId, firebaseAuthUID, data: { hearingProfile } });
};

const updateJoinMetrics = async ({
  avaId,
  firebaseAuthUID,
  joinMetrics,
}: {
  avaId: string,
  firebaseAuthUID: string,
  joinMetrics: Object,
}) => patchUser({ avaId, firebaseAuthUID, data: { joinMetrics } });

const updateInviteMetrics = async ({
  avaId,
  firebaseAuthUID,
  inviteMetrics,
}: {
  avaId: string,
  firebaseAuthUID: string,
  inviteMetrics: Object,
}) => patchUser({ avaId, firebaseAuthUID, data: { inviteMetrics } });

const updateSettings = async ({
  avaId,
  firebaseAuthUID,
  settings,
}: {
  avaId: string,
  firebaseAuthUID: string,
  settings: Object,
}) => patchUser({ avaId, firebaseAuthUID, data: { settings } });

const updateRole = async ({ avaId, firebaseAuthUID, role }: { avaId: string, firebaseAuthUID: string, role: string }) =>
  patchUser({ avaId, firebaseAuthUID, data: { role } });

const getOrCreateProfile = async ({ firebaseAuthUID }: { firebaseAuthUID: string }) => {
  return patchUser({ avaId: 'me', firebaseAuthUID, data: {} });
};

const getProfile = async ({ user }: { user?: Object }) => {
  const firebaseAuthUID = user ? user.firebaseAuthUID || user.id : null;
  const url = getUsersUrl('me', 'v1', firebaseAuthUID);
  return httpRequestWithToken({
    url,
    method: 'GET',
  });
};

const getGuestProfile = async ({ avaId, firebaseAuthUID }: { avaId: string, firebaseAuthUID: ?string }) => {
  let url = `${getAvaBackendEndpoint()}/api/v1/guest-users/${avaId}`;
  if (firebaseAuthUID) {
    url = `${url}?firebaseAuthUID=${firebaseAuthUID}`;
  }

  return httpRequestWithToken({
    url,
    method: 'GET',
  });
};

const createGuestProfile = async ({ roomId, firebaseAuthUID }: { roomId: string, firebaseAuthUID: ?string }) => {
  let url = `${getAvaBackendEndpoint()}/api/v1/guest-users/me`;
  if (firebaseAuthUID) {
    url = `${url}?firebaseAuthUID=${firebaseAuthUID}`;
  }

  return httpRequestWithToken({
    url,
    method: 'POST',
    payload: { roomId },
  });
};

export const beginChatWithUser = async (avaId: string, msg: string, firebaseAuthUID: string) => {
  // firebaseAuthUID is the firebase UID of the SCRIBE, not the user being chatted with
  const url = `${getAvaBackendEndpoint()}/api/v1.1/users/beginChat?firebaseAuthUID=${firebaseAuthUID}`;
  return httpRequestWithToken({
    url,
    method: 'POST',
    payload: { avaId, messageFromUser: '.', messageToUser: msg },
  });
};

const getUsersUrl = (avaId: string, version: string, firebaseAuthUID: ?string): string => {
  const url = `${getAvaBackendEndpoint()}/api/${version}/users/${avaId}`;
  if (firebaseAuthUID) {
    return `${url}?firebaseAuthUID=${firebaseAuthUID}`;
  }
  return url;
};

const terminateCall = async ({ user, sid }: { user: Object, sid: string }) => {
  const url = `${getAvaBackendEndpoint()}/api/v1/calls/${sid}?avaId=${user.avaId}&firebaseAuthUID=${user.id}`;
  return httpRequestWithToken({ url, method: 'DELETE' });
};

const createTwilioCall = async ({
  avaId,
  calleeInternationalNumber,
  conferenceType,
  roomId,
  sendDigits,
  speechLang,
  firebaseAuthUID,
}: {
  avaId: string,
  calleeInternationalNumber: string,
  conferenceType: ?string,
  roomId: string,
  sendDigits: Object[],
  speechLang: string,
  firebaseAuthUID: string,
}) => {
  const url = `${getAvaBackendEndpoint()}/api/v1/calls?avaId=${avaId}&firebaseAuthUID=${firebaseAuthUID}`;

  return httpRequestWithToken({
    url,
    method: 'POST',
    payload: {
      roomId,
      calleeInternationalNumber,
      sendDigits,
      speechLang,
      conferenceType,
    },
  });
};

const rateConversation = async ({
  avaId,
  firebaseAuthUID,
  rating,
  roomId,
}: {
  avaId: string,
  firebaseAuthUID: string,
  rating: number,
  roomId: string,
}) => {
  const url = `${getAvaBackendEndpoint()}/api/v1/conversation-rates?roomId=${roomId}&avaId=${avaId}&firebaseAuthUID=${firebaseAuthUID}`;

  return httpRequestWithToken({
    url,
    method: 'POST',
    payload: {
      rate: rating,
    },
  });
};

const exportCaptions = async ({
  avaId,
  firebaseAuthUID,
  apiToken,
  roomId,
}: {
  avaId: string,
  firebaseAuthUID: string,
  apiToken: string,
  roomId: string,
}) => {
  const url = `${getAvaBackendEndpoint()}/api/v1/zoom-captions-exports?avaId=${avaId}&firebaseAuthUID=${firebaseAuthUID}`;

  return httpRequestWithToken({
    url,
    method: 'POST',
    payload: { roomId, apiToken },
  });
};

const findOrganizations = async ({
  firebaseAuthUID,
  orgName,
  orgType,
}: {
  firebaseAuthUID: string,
  orgName: string,
  orgType: string,
}) => {
  const url = `${getAvaBackendEndpoint()}/api/v1/find-organizations?keywords=${orgName}&firebaseAuthUID=${firebaseAuthUID}&orgType=${orgType}`;
  return httpRequestWithToken({
    url,
    method: 'GET',
  });
};

const getUserCredits = async ({ avaId, firebaseAuthUID }: { avaId: string, firebaseAuthUID: string }) => {
  const url = `${getAvaBackendEndpoint()}/api/v1.1/user-credits/${avaId}?avaId=${avaId}&firebaseAuthUID=${firebaseAuthUID}`;
  return httpRequestWithToken({
    url,
    method: 'GET',
  });
};

const getSupports = async () => httpRequest({ url: `${getAvaBackendEndpoint()}/api/v1/supports`, method: 'GET' });

const getRoomId = async ({ token }: { token: string }) =>
  httpRequest({ url: `${getAvaBackendEndpoint()}/api/v1/tokens/${token}`, method: 'GET' });

const saveReport = async ({ report, firebaseAuthUID }: { report: Object, firebaseAuthUID: string }) =>
  httpRequestWithToken({
    url: `${getAvaBackendEndpoint()}/api/v1/reports?firebaseAuthUID=${firebaseAuthUID}`,
    method: 'POST',
    payload: report,
  });

export const users = {
  getOrCreateProfile,
  getProfile,
  updateFeatures,
  updateHearingProfile,
  updateInviteMetrics,
  updateJoinMetrics,
  updateMetrics,
  updateRole,
  updateSettings,
  updateUserName,
};

export const guestUsers = {
  getGuestProfile,
  createGuestProfile,
};

export const userCredits = {
  getUserCredits,
};

export const conversations = {
  exportCaptions,
  rateConversation,
  getRoomId,
};

export const calls = {
  createTwilioCall,
  terminateCall,
};

export const organizations = {
  findOrganizations,
};

export const supports = {
  getSupports,
};

export const reports = {
  saveReport,
};
